import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import useFileUpload from '../AttachmentsModal/hooks/useFileUpload';
import { listAttachments } from '../../../../services/attachmentService';
import { isDocUploadAttachment } from '../../../DocEditor/utils/fileOverrides';

import ModalHeader from './components/ModalHeader';
import ModeTabs from './components/ModeTabs';
import LinkMode from './components/LinkMode';
import UploadMode from './components/UploadMode';
import SelectMode from './components/SelectMode';
import ModalFooter from './components/ModalFooter';

import './styles/index.css';

/**
 * DeliverableSubmitModal Component
 * 
 * Modal for submitting a deliverable via one of three methods:
 * 1. Link - Enter a URL
 * 2. Upload - Upload a new file (becomes an attachment AND fulfills deliverable)
 * 3. Select from attachments - Choose an existing attachment
 */
const DeliverableSubmitModal = ({
  isOpen,
  onClose,
  taskId,
  taskName,
  deliverable,
  onSubmitLink,
  onSubmitAttachment,
  onAttachmentsChange,
}) => {
  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  
  // Which mode is selected: 'link' | 'upload' | 'select'
  const [mode, setMode] = useState('link');
  
  // Link mode state
  const [linkUrl, setLinkUrl] = useState('');
  
  // Select mode state
  const [attachments, setAttachments] = useState([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState(null);
  
  // Upload error state
  const [uploadError, setUploadError] = useState(null);
  
  // Handle successful upload - fulfills the deliverable with the new attachment
  const handleUploadComplete = useCallback((uploadedFile) => {
    // Notify parent of attachment count change
    onAttachmentsChange?.();
    
    // Submit the deliverable with the uploaded attachment
    onSubmitAttachment?.(deliverable.id, {
      attachmentId: uploadedFile.attachmentId,
      filename: uploadedFile.filename,
    });
    
    onClose();
  }, [deliverable?.id, onSubmitAttachment, onAttachmentsChange, onClose]);
  
  // Handle upload error
  const handleUploadError = useCallback((errorMessage) => {
    setUploadError(errorMessage);
  }, []);
  
  // Upload hook
  const {
    isDragging,
    uploadProgress,
    isUploading,
    fileInputRef,
    dropZoneRef,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileSelect,
    openFilePicker,
  } = useFileUpload({
    missionId: taskId,
    onUploadComplete: handleUploadComplete,
    onError: handleUploadError,
    onRequestNote: null, // Skip note for deliverable uploads
  });
  
  // Determine if this is an update (deliverable already completed)
  const isUpdate = deliverable?.completed;
  
  // Animation effect
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Default to appropriate mode based on existing submission
      if (deliverable?.link && !deliverable?.submittedFilename) {
        setMode('link');
        setLinkUrl(deliverable.link); // Pre-fill with existing link
      } else if (deliverable?.attachmentId) {
        setMode('upload'); // Default to upload for updating file-based submissions
        setLinkUrl('');
      } else {
        setMode('link');
        setLinkUrl('');
      }
      setSelectedAttachmentId(null);
      setUploadError(null);
      setAttachments([]); // Reset attachments so they reload fresh
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShouldAnimate(true);
        });
      });
    } else if (isVisible) {
      setShouldAnimate(false);
      const timer = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible, deliverable]);
  
  // Load attachments when switching to select mode
  useEffect(() => {
    if (mode === 'select' && taskId && attachments.length === 0) {
      setIsLoadingAttachments(true);
      listAttachments(taskId)
        .then(result => {
          setAttachments(
            (result.attachments || []).filter((a) => !isDocUploadAttachment(a))
          );
        })
        .catch(err => {
          console.error('Failed to load attachments:', err);
        })
        .finally(() => {
          setIsLoadingAttachments(false);
        });
    }
  }, [mode, taskId, attachments.length]);
  
  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isUploading) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isUploading, onClose]);
  
  // Handle link submission
  const handleSubmitLink = () => {
    if (!linkUrl.trim()) return;
    onSubmitLink?.(deliverable.id, linkUrl.trim());
    onClose();
  };
  
  // Handle selecting existing attachment
  const handleSelectAttachment = () => {
    if (!selectedAttachmentId) return;
    const attachment = attachments.find(a => a.id === selectedAttachmentId);
    if (attachment) {
      onSubmitAttachment?.(deliverable.id, {
        attachmentId: attachment.id,
        filename: attachment.filename,
      });
      onClose();
    }
  };
  
  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  if (!isVisible || !deliverable) return null;
  
  const content = (
    <div className={`deliverable-submit-modal ${shouldAnimate ? 'deliverable-submit-modal--open' : ''}`}>
      {/* Backdrop */}
      <div className="deliverable-submit-modal__backdrop" onClick={() => !isUploading && onClose()} />
      
      {/* Modal */}
      <div className="deliverable-submit-modal__container">
        {/* Header */}
        <ModalHeader
          isUpdate={isUpdate}
          deliverableName={deliverable?.name}
          onClose={onClose}
          isUploading={isUploading}
        />
        
        {/* Mode tabs */}
        <ModeTabs
          mode={mode}
          setMode={setMode}
          isUploading={isUploading}
        />
        
        {/* Content based on mode */}
        <div className="deliverable-submit-modal__content">
          {mode === 'link' && (
            <LinkMode
              linkUrl={linkUrl}
              setLinkUrl={setLinkUrl}
              onSubmit={handleSubmitLink}
              isUpdate={isUpdate}
            />
          )}
          
          {mode === 'upload' && (
            <UploadMode
              uploadError={uploadError}
              setUploadError={setUploadError}
              isDragging={isDragging}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
              dropZoneRef={dropZoneRef}
              fileInputRef={fileInputRef}
              handleDragEnter={handleDragEnter}
              handleDragLeave={handleDragLeave}
              handleDragOver={handleDragOver}
              handleDrop={handleDrop}
              handleFileSelect={handleFileSelect}
              openFilePicker={openFilePicker}
              isUpdate={isUpdate}
              deliverable={deliverable}
            />
          )}
          
          {mode === 'select' && (
            <SelectMode
              isLoadingAttachments={isLoadingAttachments}
              attachments={attachments}
              selectedAttachmentId={selectedAttachmentId}
              setSelectedAttachmentId={setSelectedAttachmentId}
              formatSize={formatSize}
            />
          )}
        </div>
        
        {/* Footer */}
        <ModalFooter
          mode={mode}
          isUpdate={isUpdate}
          isUploading={isUploading}
          linkUrl={linkUrl}
          selectedAttachmentId={selectedAttachmentId}
          onClose={onClose}
          onSubmitLink={handleSubmitLink}
          onSelectAttachment={handleSelectAttachment}
        />
      </div>
    </div>
  );
  
  return ReactDOM.createPortal(content, document.body);
};

export default DeliverableSubmitModal;
